import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { environment } from "../app/environment";

interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError(): void;
}

const ErrorFallback = ({
  error,
  componentStack,
  eventId,
  resetError,
}: ErrorFallbackProps) => {
  const { t } = useTranslation(["errorBoundary"]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-base-100 p-4">
      <div className="card w-full max-w-xl bg-base-200 shadow-xl">
        <div className="card-body text-center">
          <div className="mb-4 flex justify-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-error" />
          </div>

          <h2 className="card-title mb-4 justify-center text-2xl">
            {t("title")}
          </h2>

          <p className="mb-4 text-base-content/70">{t("description")}</p>

          <p className="mb-6 text-base-content/50 text-sm">
            {t("reportedMessage")}
            {eventId && (
              <span className="mt-1 block font-mono text-xs">
                {t("eventId")}: {eventId}
              </span>
            )}
          </p>

          <div className="card-actions justify-center gap-3">
            <button
              type="button"
              onClick={resetError}
              className="btn btn-primary"
            >
              {t("tryAgain")}
            </button>

            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn btn-outline"
            >
              {t("reloadPage")}
            </button>
          </div>

          {environment.name === "development" && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer font-semibold text-base-content/70 text-sm">
                {t("technicalDetails")}
              </summary>
              <div className="mt-2 max-h-40 overflow-auto rounded bg-base-300 p-3 font-mono text-xs">
                <div className="mb-2">
                  <strong>Error:</strong> {String(error)}
                </div>
                <div>
                  <strong>Component Stack:</strong>
                  <pre className="whitespace-pre-wrap">{componentStack}</pre>
                </div>
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
